import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  CircularProgress,
  FormControlLabel,
  Typography,
  Box,
  Grid,
  TextField,
  Divider,
  Paper,
  Avatar,
  InputAdornment,
  Switch,
  IconButton,
  Collapse,
  Chip,
  Snackbar,
  Alert
} from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import Lottie from 'lottie-react';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import StorefrontIcon from '@mui/icons-material/Storefront';
import BeenhereIcon from '@mui/icons-material/Beenhere';
import PaymentService from '../../services/PaymentService';
import { getSession, getAutoToken } from '../../utils/authUtils';
import GMBService from '../../services/GMBService';
import CongratsModal from './CongratsModal';
import PLAN_FEATURES from '../../config/planFeatures';
import ApiService from '../../services/api';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

// Note: discount coupons removed from UI; backend still supports coupons for future use

// Styled components for coupon
const CouponContainer = styled(Box)(({ theme }) => ({
  background: theme.palette.background.paper,
  borderRadius: '12px',
  padding: '12px 16px',
  border: `1px dashed ${theme.palette.primary.main}`,
  marginBottom: '16px',
  [theme.breakpoints.down('sm')]: {
    padding: '10px 12px',
    borderRadius: '10px'
  }
}));

const ApplyButton = styled(Button)(({ theme }) => ({
  background: theme.palette.primary.main,
  color: theme.palette.primary.contrastText || '#fff',
  fontWeight: 600,
  padding: '8px 20px',
  borderRadius: '8px',
  textTransform: 'none',
  minWidth: '80px',
  '&:hover': {
    background: theme.palette.primary.dark,
  },
  '&:disabled': {
    background: '#ccc',
    color: '#666'
  },
  [theme.breakpoints.down('sm')]: {
    padding: '6px 14px',
    minWidth: '70px',
    fontSize: '13px'
  }
}));

const CheckoutModal = ({ open, onClose, planId, billingCycle, amount, isTrial, pendingPaymentData, pendingSubscriptionData }) => {
  const [loading, setLoading] = useState(false);
  const [profiles, setProfiles] = useState([]);
  const [selectedProfileId, setSelectedProfileId] = useState(null);
  const [error, setError] = useState(null);
  const [payeeMobile, setPayeeMobile] = useState('');
  const [payeeGmail, setPayeeGmail] = useState('');
  const [sessionEmail, setSessionEmail] = useState(null);
  const [addNumberToGMB, setAddNumberToGMB] = useState(false);

  // Discount coupon removed from UI — only trial token handled here
  // Trial coupon states (new trial coupon input in checkout)
  const [trialCode, setTrialCode] = useState('');
  const [showTrialField, setShowTrialField] = useState(false);
  const [trialError, setTrialError] = useState('');
  const [trialLoading, setTrialLoading] = useState(false);
  const [trialMessage, setTrialMessage] = useState('');
  const [trialSuccess, setTrialSuccess] = useState(false);
  const [serverPendingPayment, setServerPendingPayment] = useState(null);
  const [serverPendingSubscription, setServerPendingSubscription] = useState(null);
  const [trialTokenApplied, setTrialTokenApplied] = useState(null);

  // Trial confirmation modal state (styled replacement for window.confirm)
  const [trialConfirmOpen, setTrialConfirmOpen] = useState(false);
  const [trialConfirmData, setTrialConfirmData] = useState(null);
  const [mandateInfo, setMandateInfo] = useState(null);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [processingMessage, setProcessingMessage] = useState('Processing your payment and activating access...');
  const [processingProgress, setProcessingProgress] = useState(0);
  const [processingAnimationData, setProcessingAnimationData] = useState(null);
  const [congratsOpen, setCongratsOpen] = useState(false);
  const [congratsPlan, setCongratsPlan] = useState(null);
  const [congratsPlanLabel, setCongratsPlanLabel] = useState('');
  const [congratsFeatures, setCongratsFeatures] = useState([]);

  // Validation error states
  const [emailError, setEmailError] = useState('');

  // Snackbar states for plan notifications
  const [planSnackbarOpen, setPlanSnackbarOpen] = useState(false);
  const [planSnackbarMessage, setPlanSnackbarMessage] = useState('');
  const [phoneError, setPhoneError] = useState('');

  const theme = useTheme();
  const navigate = useNavigate();

  const formatPlanLabel = (planName) => {
    if (!planName) return 'Active plan';
    return planName
      .toString()
      .replace(/_/g, ' ')
      .split(/\s+/)
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ');
  };

  useEffect(() => {
    if (!open) {
      setError(null);
      setPlanSnackbarOpen(false);
      setPlanSnackbarMessage('');
    }
  }, [open]);

  const handlePlanSnackbarClose = () => {
    setPlanSnackbarOpen(false);
  };

  const formatExpiryDate = (value) => {
    if (!value) return null;
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return null;
    return parsed.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const showActivePlanNotice = (profile, payload = {}) => {
    const profileId = profile?.id || profile?.location_id || profile?.locationId;
    if (!profileId) return;
    const planName = payload.plan || profile?.activePlanDetails?.plan || profile?.plan || profile?.planName || 'Active plan';
    const expiresAt = payload.expiresAt || profile?.activePlanDetails?.expiresAt || profile?.expiresAt || profile?.expiration;
    setError(null);
    setPlanSnackbarMessage(`${profile?.name || 'This profile'} already has the ${formatPlanLabel(planName)} active.`);
    setPlanSnackbarOpen(true);
  };

  // Animate processing progress when dialog is open
  useEffect(() => {
    let iv = null;
    if (processingPayment) {
      setProcessingProgress(p => (p > 0 ? p : 5));
      iv = setInterval(() => {
        setProcessingProgress(p => {
          // advance randomly but cap at 95 until finished
          if (p >= 95) return p;
          const next = p + Math.floor(Math.random() * 8) + 3;
          return Math.min(95, next);
        });
      }, 700);
    } else {
      // when closed, fill to 100 briefly then reset
      if (processingProgress > 0 && processingProgress < 100) setProcessingProgress(100);
      const t = setTimeout(() => setProcessingProgress(0), 600);
      return () => clearTimeout(t);
    }
    return () => { if (iv) clearInterval(iv); };
  }, [processingPayment, processingProgress]);

  // Load Lottie animation data when processing starts
  useEffect(() => {
    let cancelled = false;
    const url = 'https://assets9.lottiefiles.com/packages/lf20_jtbfg2nb.json';
    async function load() {
      try {
        const res = await fetch(url);
        if (!res.ok) throw new Error('Failed to fetch animation');
        const json = await res.json();
        if (!cancelled) setProcessingAnimationData(json);
      } catch (e) {
        console.warn('Failed to load Lottie animation:', e.message || e);
      }
    }
    if (processingPayment && !processingAnimationData) load();
    return () => { cancelled = true; };
  }, [processingPayment, processingAnimationData]);

  // Determine a safe logo URL for Razorpay checkout. Avoid using localhost images
  // inside Razorpay's hosted checkout iframe to prevent CORS/address-space issues.
  function getLogoUrl() {
    try {
      const hostname = window && window.location && window.location.hostname ? window.location.hostname : '';
      if (hostname.includes('localhost') || hostname.startsWith('127.')) {
        return ''; // avoid local asset to prevent Razorpay CORS errors
      }
      return (window.location.origin || '') + '/logo.png';
    } catch (e) {
      return '';
    }
  }
  // Map planId + billingCycle to numeric amounts (if frontend needs to pass explicit amount)
  function resolveAmount(plan, billing) {
    const priceMap = {
      monthly: { basic: 1199, advanced: 1999, pro: 2999 },
      annually: { basic: 11999, advanced: 19999, pro: 29999 },
      yearly: { basic: 11999, advanced: 19999, pro: 29999 }
    };
    const cycle = billing || 'annually';
    const planKey = (plan || '').toLowerCase();
    return priceMap[cycle] && priceMap[cycle][planKey] ? priceMap[cycle][planKey] : null;
  }

  const baseAmount = resolveAmount(planId, billingCycle) || 0;
  const addonAmount = addNumberToGMB ? 5000 : 0;
  const subtotal = Number(baseAmount) + addonAmount;
  const discountAmount = 0; // discounts handled server-side; frontend UI removed
  const computedTotal = subtotal - discountAmount;

  const isTrialCheckout = Boolean(isTrial || serverPendingPayment || serverPendingSubscription || pendingPaymentData || pendingSubscriptionData || trialTokenApplied || (Number(amount) === 1));
  const planAmount = Number(resolveAmount(planId, billingCycle) || baseAmount || 0);
  const trialDays = 7;
  const firstChargeDate = new Date(Date.now() + trialDays * 24 * 60 * 60 * 1000);
  const firstChargeDateStr = firstChargeDate.toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: 'numeric' });

  // Discount coupon removed from UI — no client-side discount handler

  // Handle trial coupon application from checkout (relocated from pricing page)
  const handleApplyTrialCoupon = async () => {
    const code = (trialCode || '').toString().trim();
    setTrialError('');
    if (!code) {
      setTrialError('Please enter a coupon code');
      return;
    }

    // Quick client-side fixed coupon support
    if (code.toUpperCase() === 'E2EVISIBEEN') {
      const fixed = code.toUpperCase();
      setTrialTokenApplied(fixed);
      setTrialError('');
      setTrialMessage('Trial coupon applied — proceed to verification');
      setTrialSuccess(true);
      try { localStorage.setItem('came_from_trial', fixed); localStorage.setItem('came_from_trial_ts', String(Date.now())); } catch (e) { }
      return;
    }

    setTrialLoading(true);
    try {
      const apiBase = process.env.REACT_APP_API_BASE || (window.location.hostname === 'localhost' ? 'http://localhost:5000' : '');
      const token = getAutoToken();
      if (!token) {
        try { localStorage.setItem('pending_trial_coupon', code); } catch (e) { }
        setTrialError('Please sign in to claim the coupon. Redirecting to login...');
        setTimeout(() => { navigate(`/login?next=${encodeURIComponent(window.location.pathname)}`); }, 700);
        setTrialLoading(false);
        return;
      }

      const resp = await fetch(`${apiBase}/api/v1/customer/auth/claim-trial`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ trialToken: code })
      });
      const json = await resp.json();
      if (!resp.ok) {
        setTrialError(json?.message || json?.error || (json && typeof json === 'string' ? json : 'Failed to claim coupon'));
      } else if (json && (json.pendingPayment || (json.data && json.data.pendingPayment))) {
        const paymentInfo = (json.payment || (json.data && json.data.payment)) || null;
        try { localStorage.setItem('came_from_trial', code); } catch (e) { }
        setServerPendingPayment(paymentInfo);
        setTrialTokenApplied(code.toUpperCase());
        setTrialMessage('Verification payment pending — proceed to pay ₹1');
        setTrialSuccess(true);
        try { localStorage.setItem('came_from_trial_ts', String(Date.now())); } catch (e) { }
      } else if (json && (json.subscriptionInit || (json.data && json.data.subscriptionInit))) {
        const subscriptionData = json.subscriptionInit || (json.data && json.data.subscriptionInit);
        try { localStorage.setItem('came_from_trial', code); } catch (e) { }
        setServerPendingSubscription(subscriptionData);
        setTrialTokenApplied(code.toUpperCase());
        setTrialMessage('Trial initialized — proceed to checkout to complete verification');
        setTrialSuccess(true);
        try { localStorage.setItem('came_from_trial_ts', String(Date.now())); } catch (e) { }
      } else {
        setTrialTokenApplied(code.toUpperCase());
        setTrialMessage('Trial coupon applied — proceed to verification');
        setTrialSuccess(true);
        try { localStorage.setItem('came_from_trial', code.toUpperCase()); localStorage.setItem('came_from_trial_ts', String(Date.now())); } catch (e) { }
      }
    } catch (e) {
      setTrialError(e?.message || 'Error applying coupon');
    } finally {
      setTrialLoading(false);
    }
  };

  // Discount coupon removal is not exposed in UI (removed)

  const handleRemoveTrial = () => {
    setTrialTokenApplied(null);
    setServerPendingPayment(null);
    setServerPendingSubscription(null);
    setTrialCode('');
    setTrialError('');
    setTrialMessage('');
    setTrialSuccess(false);
    try { localStorage.removeItem('came_from_trial'); localStorage.removeItem('came_from_trial_ts'); } catch (e) { }
  };

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setError(null);

    const fetchProfiles = async () => {
      try {
        const session = getSession();
        const email = session?.email || session?.user?.email || session?.data?.email;
        setSessionEmail(email || null);
        if (email) setPayeeGmail(email);
        if (!email) {
          setProfiles([]);
          setError('No user email found. Please login first.');
          setLoading(false);
          return;
        }

        // 1) Prefer cached Google profiles scoped to this owner
        try {
          const cached = localStorage.getItem('availableGMBProfiles');
          const owner = localStorage.getItem('availableGMBProfilesOwner');
          if (cached && owner && String(owner).toLowerCase() === String(email).toLowerCase()) {
            const parsed = JSON.parse(cached);
            if (Array.isArray(parsed) && parsed.length > 0) {
              setProfiles(parsed);
              // Auto-select first profile without an active plan, or fall back to first profile
              const availableProfile = parsed.find(p => !p.hasActivePlan) || parsed[0];
              const profileId = availableProfile.id || availableProfile.location_id || availableProfile.locationId;
              setSelectedProfileId(profileId);
              console.log('[CheckoutModal] Auto-selected profile from cache:', profileId);
              setLoading(false);
              return;
            }
          }
        } catch (e) {
          // ignore cache parse errors and continue
          console.warn('Failed to read cached GMB profiles:', e);
        }

        // 2) If we have a Google access token, fetch accounts & locations directly
        try {
          const accessToken = (() => {
            try { return localStorage.getItem('googleAccessToken'); } catch (_) { return null; }
          })();

          if (accessToken) {
            const accounts = await GMBService.getAccounts(accessToken);
            const allLocations = [];
            for (const acct of accounts) {
              try {
                if (!acct?.name) continue;
                const locs = await GMBService.getLocations(accessToken, acct.name);
                if (Array.isArray(locs) && locs.length) {
                  locs.forEach(l => allLocations.push({ account: acct, loc: l }));
                }
              } catch (e) {
                console.warn('Failed to fetch locations for account', acct?.name, e?.message || e);
              }
            }

            if (allLocations.length > 0) {
              const formatted = allLocations.map(({ account, loc }) => ({
                id: loc.name?.split('/').pop(),
                name: loc.title || loc.locationName || 'Unnamed Location',
                address: loc.storefrontAddress ? (loc.storefrontAddress.addressLines?.join(', ') || '') : (loc.address || ''),
                locationId: loc.name?.split('/').pop(),
                accountName: account.accountName || 'Business Account',
                verificationState: account.verificationState || 'UNKNOWN',
                locationData: loc,
                accountData: account,
                fullLocationName: loc.name
              }));

              // Cache for later
              try {
                localStorage.setItem('availableGMBProfiles', JSON.stringify(formatted));
                localStorage.setItem('availableGMBProfilesOwner', String(email));
              } catch (_) { }

              setProfiles(formatted);
              // Auto-select first profile without an active plan, or fall back to first profile
              const availableProfile = formatted.find(p => !p.hasActivePlan) || formatted[0];
              const profileId = availableProfile.id || availableProfile.locationId || availableProfile.location_id;
              setSelectedProfileId(profileId);
              console.log('[CheckoutModal] Auto-selected profile from Google API:', profileId);
              setLoading(false);
              return;
            }
          }
        } catch (googleErr) {
          console.warn('Google fetch failed (will fallback to DB):', googleErr?.message || googleErr);
        }

        // 3) Fallback: query backend DB for profiles (some users have synced accounts)
        const api = await import('../../services/api').then(m => m.default);
        const resp = await api.get(`/admin/gmb-account/user-by-email?email=${encodeURIComponent(email)}`);

        // Parse response - handle various response structures
        let found = [];
        if (Array.isArray(resp)) {
          found = resp;
        } else if (resp?.profiles && Array.isArray(resp.profiles)) {
          found = resp.profiles;
        } else if (resp?.data?.profiles && Array.isArray(resp.data.profiles)) {
          found = resp.data.profiles;
        } else if (resp?.data && Array.isArray(resp.data)) {
          found = resp.data;
        }

        console.log('[CheckoutModal] Fetched profiles:', found.length, 'profiles');
        setProfiles(found);

        // Auto-select first profile that doesn't have an active plan (if info available)
        if (found.length > 0) {
          const availableProfile = found.find(p => !p.hasActivePlan) || found[0];
          const profileId = availableProfile.id || availableProfile.location_id || availableProfile.locationId;
          setSelectedProfileId(profileId);
          console.log('[CheckoutModal] Auto-selected profile:', profileId);
        }

      } catch (err) {
        console.error('Failed to fetch profiles for checkout:', err);
        setError(err.message || 'Failed to fetch profiles');
      } finally {
        setLoading(false);
      }
    };

    fetchProfiles();
  }, [open]);

  // Effect to check plan status for loaded profiles (especially those from Google API which lack DB plan info)
  useEffect(() => {
    if (!profiles || profiles.length === 0) return;

    // Check if we need to verify plans (if any profile is missing plan info check)
    // We use a flag 'planChecked' to avoid infinite loops, or strictly check for null 'hasActivePlan'
    const needsCheck = profiles.some(p => p.hasActivePlan === undefined);

    if (needsCheck) {
      const checkPlans = async () => {
        try {
          const locationIds = profiles.map(p => p.id || p.location_id || p.locationId).filter(Boolean);
          if (locationIds.length === 0) return;

          const res = await ApiService.post('/admin/gmb-account/check-plans', { location_ids: locationIds });
          const results = res.results || res.data?.results || {};

          setProfiles(currentProfiles =>
            currentProfiles.map(p => {
              const pid = p.id || p.location_id || p.locationId;
              const status = results[pid];
              if (status) {
                return {
                  ...p,
                  hasActivePlan: status.hasActivePlan,
                  activePlanDetails: status.hasActivePlan ? { plan: status.plan, expiresAt: status.expiresAt } : null,
                  planChecked: true
                };
              }
              return { ...p, planChecked: true }; // marked as checked even if no result found
            })
          );

          // Auto-select first profile if none selected (allow all profiles regardless of plan status)
          if (!selectedProfileId && profiles.length > 0) {
            const firstProfile = profiles[0];
            const newPid = firstProfile.id || firstProfile.location_id || firstProfile.locationId;
            setSelectedProfileId(newPid);
            console.log('[CheckoutModal] Auto-selected first profile:', newPid);
          }

        } catch (err) {
          console.warn('Failed to check existing plans:', err);
        }
      };

      checkPlans();
    }
  }, [profiles, selectedProfileId]);

  const handleProfileClick = (profile) => {
    const profileId = profile?.id || profile?.location_id || profile?.locationId;
    if (!profileId) return;
    // Allow selection of any profile (removed active plan restriction)
    setSelectedProfileId(profileId);
  };



  const handleBuy = async () => {
    setError(null);
    setEmailError('');
    setPhoneError('');

    let hasError = false;

    // Validate email
    if (!payeeGmail) {
      setEmailError('Email is required');
      hasError = true;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(payeeGmail)) {
      setEmailError('Please enter a valid email address');
      hasError = true;
    }

    // Validate phone
    if (!payeeMobile) {
      setPhoneError('Mobile number is required');
      hasError = true;
    } else if (!/^\+?[0-9]{7,15}$/.test(payeeMobile)) {
      setPhoneError('Please enter a valid mobile number');
      hasError = true;
    }

    // Validate profile selection
    if (!selectedProfileId) {
      setError('Please select a Business Profile to continue');
      hasError = true;
    }

    if (hasError) {
      return;
    }

    setLoading(true);
    try {
      const session = getSession();
      const email = session?.email || session?.user?.email || session?.data?.email;
      const notes = {
        profile_id: selectedProfileId,
        profile_plan: planId,
        billing_cycle: billingCycle
      };

      const profileInfo = profiles.find(p => String(p.id) === String(selectedProfileId)) || {};
      const metadata = {
        notes: {
          ...notes,
          payeeGmail,
          payeeMobile,
          user_email: sessionEmail || payeeGmail,
          user_phone: payeeMobile,
          // Coupon information
          coupon_code: trialTokenApplied || null,
          coupon_discount_percent: 0,
          coupon_discount_amount: 0,
          original_amount: subtotal
        },
        userEmail: sessionEmail || payeeGmail,
        userPhone: payeeMobile,
        userId: session?.id || session?.user?.id || session?.data?.id,
        businessName: profileInfo.name || profileInfo.title || profileInfo.business_name,
        profileEmail: profileInfo.email || profileInfo.gmail,
        profilePhone: profileInfo.primaryPhone || profileInfo.phone || profileInfo.telephone,
        payeeMobile,
        payeeGmail,
        addNumberToGMB,
        couponCode: trialTokenApplied || null,
        couponDiscount: 0,
        discountAmount
      };

      // Resolve amount if not provided
      let resolvedAmount = amount;
      if (!resolvedAmount) resolvedAmount = resolveAmount(planId, billingCycle);
      // Add extra charge if user opted to add number to GMB, then subtract discount
      const extra = addNumberToGMB ? 5000 : 0;
      const amountBeforeDiscount = Number(resolvedAmount) + Number(extra);
      const discount = 0; // frontend discount removed; server-side only
      let finalAmount = amountBeforeDiscount - discount;

      // If a coupon code is applied, treat this as the trial flow and charge ₹1
      // upfront for verification/autopay setup. Backend also creates pending
      // subscription/payment entries when a coupon is claimed.
      const couponInMetadata = metadata && (metadata.couponCode || metadata.coupon_code);
      if (couponInMetadata) {
        finalAmount = 1;
      }

      // If a server-created pending payment exists (from claim-trial fallback), use it to open checkout
      const effectivePendingPayment = serverPendingPayment || pendingPaymentData;
      if (effectivePendingPayment && (effectivePendingPayment.orderId || effectivePendingPayment.order_id)) {
        try {
          await PaymentService.loadRazorpayScript();
          const key = effectivePendingPayment.keyId || effectivePendingPayment.key || effectivePendingPayment.key_id || (window && window.__RAZORPAY_KEY__);
          const orderId = effectivePendingPayment.orderId || effectivePendingPayment.order_id || effectivePendingPayment.orderId;
          const amt = effectivePendingPayment.amount || effectivePendingPayment.amount_inr || effectivePendingPayment.amount_in_paise || null;
          // If a trial coupon/token is applied, force ₹1 (100 paise) verification amount and clear wording
          const isTrialCheckoutFlag = Boolean(couponInMetadata || trialTokenApplied || isTrialCheckout);
          const amountPaise = isTrialCheckoutFlag ? 100 : (amt ? (Number(amt) > 1000 ? Number(amt) : Number(amt) * 100) : (Number(amount || 1) * 100));
          const options = {
            key: key,
            amount: amountPaise,
            currency: pendingPaymentData.currency || 'INR',
            order_id: orderId,
            name: profiles?.[0]?.name || 'Visibeen',
            description: isTrialCheckoutFlag ? 'Trial verification — ₹1 today (7‑day trial). Full mandate will be created.' : 'Trial verification',
            image: getLogoUrl(),
            prefill: { email: payeeGmail || sessionEmail, contact: payeeMobile },
            theme: { color: theme.palette.primary.main }
          };

          await PaymentService.openCheckout(options, async (response) => {
            try {
              await PaymentService.verifyPayment(response);
              // Show congrats modal with plan features instead of immediate navigation
              const planKey = (planId || 'basic').toLowerCase();
              const cfg = PLAN_FEATURES[planKey] || PLAN_FEATURES.basic;
              setCongratsPlan(planKey);
              setCongratsPlanLabel(cfg.label || planKey.toUpperCase());
              setCongratsFeatures(cfg.features || []);
              setCongratsOpen(true);
              onClose && onClose({ success: true, result: { paymentId: response.razorpay_payment_id, orderId: response.razorpay_order_id, type: 'trial' } });
            } catch (verErr) {
              setError(verErr?.message || 'Verification failed');
              onClose && onClose({ success: false, error: 'verification_failed' });
            }
          }, (err) => {
            setError(err?.message || 'Payment cancelled');
            onClose && onClose({ success: false, error: 'payment_cancelled' });
          });
          return;
        } catch (pe) {
          console.warn('Failed to open server-created pending payment checkout:', pe);
          // fallback to normal flow
        }
      }

      // If backend already initialized a deferred subscription for trial, use it directly
      const effectivePendingSubscription = serverPendingSubscription || pendingSubscriptionData;
      if (effectivePendingSubscription && (effectivePendingSubscription.subscriptionId || effectivePendingSubscription.subscription_id)) {
        try {
          // Normalize response and reuse the existing trial confirmation modal flow
          const resp = effectivePendingSubscription;
          const trialAmount = resp.amount || (resp.breakdown && resp.breakdown.addonPaise ? Number(resp.breakdown.addonPaise) / 100 : 1);
          const planAmount = Number(resolveAmount(planId, billingCycle) || 0);
          const trialDays = 7;
          const firstChargeDate = new Date(Date.now() + trialDays * 24 * 60 * 60 * 1000);
          const dateStr = firstChargeDate.toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: 'numeric' });
          const cycleLabel = (billingCycle && billingCycle.toLowerCase().includes('year')) ? 'Yearly' : 'Monthly';

          setTrialConfirmData({
            resp,
            trialAmount: Number(trialAmount),
            planAmount,
            trialDays,
            dateStr,
            cycleLabel,
            planId,
            billingCycle
          });
          setTrialConfirmOpen(true);
          setLoading(false);
          return;
        } catch (e) {
          console.warn('Failed to prepare pending subscription trial confirmation:', e && e.message);
        }
      }

      // If this is a trial flow (small upfront verification payment), create a trial order instead
      // The trial verification amount is ₹1; also allow explicit `isTrial` flag.
      // Only treat the checkout as a trial when the user actually arrived via a trial link
      // (detected via URL param, cookie or localStorage). This prevents showing the trial
      // landing page to normal users.
      let _trialToken = null;
      try {
        if (typeof window !== 'undefined') {
          // Prefer explicit token in URL (real trial link)
          const urlToken = (function () { try { const u = new URL(window.location.href); return u.searchParams.get('trialToken') || u.searchParams.get('trialtoken') || u.searchParams.get('token'); } catch (_) { return null; } })();
          // Otherwise consider stored token but only if it was set recently
          const storedToken = (function () { try { return localStorage.getItem('came_from_trial') || localStorage.getItem('came_from_trial_token'); } catch (_) { return null; } })();
          const storedTs = (function () { try { return parseInt(localStorage.getItem('came_from_trial_ts') || '0', 10); } catch (_) { return 0; } })() || 0;
          const isRecent = storedTs && ((Date.now() - storedTs) < (1000 * 60 * 60)); // 1 hour
          _trialToken = urlToken || (isRecent ? storedToken : null);
        }
      } catch (e) { _trialToken = null; }
      const hasTrialToken = Boolean(_trialToken && String(_trialToken).trim().length > 0);
      if ((isTrial || Number(amount) === 1) && hasTrialToken) {
        console.log('Starting trial subscription flow...');
        // Ensure server has a short-lived trial context cookie (trial_ctx) set.
        // This endpoint validates the trialToken+location and sets httpOnly cookie required by /start-trial
        // Resolve trial token from multiple fallbacks (localStorage, cookies, multiple URL param names)
        let trialToken = null;
        let trialSig = null;
        try {
          if (typeof window !== 'undefined') {
            trialToken = (function () { try { return localStorage.getItem('came_from_trial'); } catch (_) { return null; } })() ||
              (function () { try { return localStorage.getItem('came_from_trial_token'); } catch (_) { return null; } })() ||
              (function () { try { const m = document.cookie.split('; ').find(c => c.trim().startsWith('came_from_trial=')); return m ? decodeURIComponent(m.split('=')[1]) : null; } catch (_) { return null; } })() ||
              (function () { try { const u = new URL(window.location.href); return u.searchParams.get('trialToken') || u.searchParams.get('trialtoken') || u.searchParams.get('came_from_trial') || u.searchParams.get('came_from_trial_token') || u.searchParams.get('token'); } catch (_) { return null; } })();

            // signature fallback from localStorage or cookies
            trialSig = (function () { try { return localStorage.getItem('came_from_trial_sig'); } catch (_) { return null; } })() || (function () { try { const m = document.cookie.split('; ').find(c => c.trim().startsWith('came_from_trial_sig=')); return m ? decodeURIComponent(m.split('=')[1]) : null; } catch (_) { return null; } })() || null;
          }
        } catch (_) { trialToken = null; trialSig = null; }

        // Resolve locationId from fallbacks: explicit localStorage saved on landing, URL param, or selected profile
        let locationId = null;
        try { locationId = (typeof window !== 'undefined' && localStorage.getItem('came_from_trial_location')) || null; } catch (_) { locationId = null; }
        if (!locationId) {
          try { const u = new URL(window.location.href); locationId = u.searchParams.get('locationId') || u.searchParams.get('location') || u.searchParams.get('locationid') || null; } catch (_) { }
        }
        if (!locationId && selectedProfileId) {
          // try to extract from selected profile info if it contains a fullLocationName like accounts/.../locations/LOCID
          const profileInfo = profiles.find(p => String(p.id) === String(selectedProfileId)) || {};
          locationId = profileInfo.locationId || profileInfo.location_id || profileInfo.fullLocationName || profileInfo.full_location_name || null;
          if (locationId && typeof locationId === 'string' && locationId.includes('/')) {
            const parts = locationId.split('/'); locationId = parts[parts.length - 1];
          }
        }

        // Allow fallback: if token or location is missing, continue and let PaymentService.startTrial use body-token fallback
        try {
          console.log('Resolved trial context for checkout preflight:', { trialToken, trialSig, locationId });
        } catch (e) { }
        if (!trialToken || !locationId) {
          console.warn('Trial validation missing inputs (will fall back to body token):', { trialToken: Boolean(trialToken), locationId });
        }
        try {
          const apiBase = process.env.REACT_APP_API_BASE || (window.location.hostname === 'localhost' ? 'http://localhost:5000' : '');
          const ctxUrl = `${apiBase}/api/v1/gmb/trial/context?locationId=${encodeURIComponent(locationId)}&trialToken=${encodeURIComponent(trialToken)}`;
          console.log('Validating trial context with', { ctxUrl });
          const ctxResp = await fetch(ctxUrl, { credentials: 'include' });
          // If server responds not ok, read body for message
          if (!ctxResp.ok) {
            const txt = await ctxResp.text().catch(() => '');
            console.error('Trial context fetch failed (non-OK):', ctxResp.status, txt);
            throw new Error(txt || `Failed to validate trial context (status ${ctxResp.status})`);
          }
          const ctxBody = await ctxResp.json().catch(() => ({}));
          // Log backend response for easier debugging
          console.log('Trial context response:', ctxBody);
          // Backend uses { error: null, code: 200, data: {...}, message }
          const ok = ctxBody && (ctxBody.error === null || ctxBody.error === undefined) && ctxBody.data && ctxBody.data.showTrial;
          if (!ok) {
            const serverMessage = (ctxBody && (ctxBody.message || ctxBody.error)) || JSON.stringify(ctxBody || {});
            console.error('Trial not available from server:', serverMessage);
            throw new Error(serverMessage || 'Trial not available');
          }
        } catch (ctxErr) {
          console.warn('Trial context validation failed, proceeding with fallback trial flow:', ctxErr?.message || ctxErr);
          // proceed without preflight — PaymentService.startTrial will attempt body fallback or use profileKey
        }

        // Decide which profileKey to pass: always use selectedProfileId if available
        const callProfileKey = selectedProfileId || null;
        // Extract profile information for the selected profile
        const selectedProfile = profiles.find(p => String(p.id) === String(selectedProfileId)) || {};
        const profileName = selectedProfile.name || selectedProfile.title || selectedProfile.business_name;
        const accountId = selectedProfile.accountData?.name?.split('/').pop() || selectedProfile.accountId;
        const extractedLocationId2 = selectedProfile.locationId || selectedProfile.id;

        try { console.log('Calling PaymentService.startTrial with', { planId, billingCycle, callProfileKey, payeeGmail, payeeMobile, profileName, accountId, extractedLocationId2 }); } catch (e) { }

        // Start a trial subscription on the backend which returns a Razorpay subscription id
        const resp = await PaymentService.startTrial(planId, billingCycle, callProfileKey, payeeGmail, payeeMobile, null, profileName, accountId, extractedLocationId2);
        const trialAmount = Number(resp && resp.amount ? resp.amount : 1); // verification amount (₹1)
        const planAmount = Number(resolveAmount(planId, billingCycle) || 0);
        const trialDays = 7; // server-side config is 7 days
        const firstChargeDate = new Date(Date.now() + trialDays * 24 * 60 * 60 * 1000);
        const dateStr = firstChargeDate.toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: 'numeric' });
        const cycleLabel = (billingCycle && billingCycle.toLowerCase().includes('year')) ? 'Yearly' : 'Monthly';

        if (!resp || !resp.subscriptionId) {
          throw new Error('Failed to initialize trial subscription');
        }

        // Prepare confirmation dialog data and open styled modal (replacing window.confirm)
        setTrialConfirmData({
          resp,
          trialAmount,
          planAmount,
          trialDays,
          dateStr,
          cycleLabel,
          planId,
          billingCycle
        });
        setTrialConfirmOpen(true);
        // stop loading - user will confirm in modal
        setLoading(false);
        return;
      }

      // SUBSCRIPTION FLOW - Autopay is always enabled (like Netflix/Spotify)
      console.log('Creating subscription with autopay...');

      // Determine if coupon/trial flow should be used. Prefer server-created
      // pending subscription when available, otherwise call startTrial which
      // creates a deferred subscription with a small addon for verification (₹1).
      let subscriptionData = null;
      // Determine trial flow from the computed finalAmount (UI): if finalAmount === 1
      // treat this as a trial verification flow and use backend trial subscription.
      const isTrialFlow = (Number(finalAmount) === 1) || Boolean(isTrial) || Boolean(serverPendingSubscription) || Boolean(pendingSubscriptionData);
      try {
        if (isTrialFlow) {
          if (serverPendingSubscription || pendingSubscriptionData) {
            subscriptionData = serverPendingSubscription || pendingSubscriptionData;
          } else {
            // Ask backend to create deferred subscription for trial (includes addon).
            // Prefer passing an explicit trial token (from localStorage or applied coupon)
            let explicitTrialToken = null;
            try { explicitTrialToken = localStorage.getItem('came_from_trial') || localStorage.getItem('came_from_trial_token') || null; } catch (_) { explicitTrialToken = null; }
            if (!explicitTrialToken) {
              explicitTrialToken = trialTokenApplied || metadata?.couponCode || metadata?.coupon_code || null;
            }
            // Extract profile information for the selected profile
            const selectedProfile = profiles.find(p => String(p.id) === String(selectedProfileId)) || {};
            const profileName = selectedProfile.name || selectedProfile.title || selectedProfile.business_name;
            const accountId = selectedProfile.accountData?.name?.split('/').pop() || selectedProfile.accountId;
            const extractedLocationId = selectedProfile.locationId || selectedProfile.id;

            subscriptionData = await PaymentService.startTrial(planId, billingCycle, selectedProfileId || null, payeeGmail, payeeMobile, explicitTrialToken, profileName, accountId, extractedLocationId);
          }
        } else {
          // Normal subscription creation (no trial coupon)
          subscriptionData = await PaymentService.createSubscription({
            planId,
            billingCycle,
            userEmail: payeeGmail,
            userPhone: payeeMobile,
            userId: metadata.userId,
            notes: metadata.notes,
            addonAmount: addNumberToGMB ? 5000 : 0,
            profileKey: selectedProfileId,
            profileName: profileInfo.name || profileInfo.title || profileInfo.business_name,
            accountId: profileInfo.account_id || profileInfo.accountId,
            locationId: profileInfo.location_id || profileInfo.locationId
          });
        }

        console.log('Subscription data ready:', subscriptionData);

        // Open Razorpay subscription checkout using server-provided subscriptionData
        await PaymentService.loadRazorpayScript();
        const logoUrl = getLogoUrl();
        const serverAmountPaise = subscriptionData && (subscriptionData.amount || subscriptionData.amount === 0) ? Number(subscriptionData.amount) : null;
        const planDisplayAmount2 = serverAmountPaise !== null ? (Number(serverAmountPaise) / 100) : (resolveAmount(planId, billingCycle) || 0);
        const planCycleLabel2 = (billingCycle && billingCycle.toLowerCase().includes('year')) ? 'Yearly' : 'Monthly';
        const subscriptionOptions = {
          key: subscriptionData.keyId,
          subscription_id: subscriptionData.subscription_id || subscriptionData.subscriptionId,
          name: metadata.businessName || 'Visibeen',
          description: couponInMetadata || trialTokenApplied
            ? `Trial: ₹1 today (verification). ${planCycleLabel2} — ₹${Number(resolveAmount(planId, billingCycle) || 0).toLocaleString('en-IN')} billed after 7 days.`
            : `${planId?.toUpperCase()} Plan - ${planCycleLabel2} — ₹${Number(planDisplayAmount2).toLocaleString('en-IN')} billed ${planCycleLabel2.toLowerCase()}`,
          image: logoUrl || undefined,
          prefill: {
            email: payeeGmail,
            contact: payeeMobile
          },
          theme: { color: theme.palette.primary.main },
          recurring: true,
          handler: async (response) => {
            console.log('Subscription payment success:', response);
            setProcessingMessage('Verifying payment and activating your access. This may take a few seconds...');
            setProcessingProgress(5);
            setProcessingPayment(true);
            try {
              const verifyResp = await PaymentService.verifySubscriptionPayment({
                razorpay_subscription_id: response.razorpay_subscription_id || subscriptionData.subscriptionId || subscriptionData.subscription_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature
              });
              console.log('Payment verified and recorded successfully', verifyResp);
              try { const { toast } = await import('react-toastify'); if (verifyResp?.whitelisted) { toast.info(`Access granted to ${verifyResp?.email || metadata?.userEmail || payeeGmail || 'your email'}`); } } catch (_) { }

              // Persist plan assignment to backend (best-effort)
              try {
                const api = await import('../../services/api').then(m => m.default);
                await api.post('/customer/profile/assign-plan', {
                  profileId: selectedProfileId,
                  planTier: planId,
                  source: 'subscription',
                  subscriptionId: subscriptionData.subscriptionId || subscriptionData.subscription_id
                });
                try {
                  const trackingApi = api;
                  const token = sessionStorage.getItem('trial_invite') || localStorage.getItem('trial_invite') || null;
                  await trackingApi.post('/tracking/convert', {
                    token: token || null,
                    source: 'subscription',
                    userId: verifyResp?.userId || verifyResp?.id || null,
                    plan: planId,
                    amount: subscriptionData?.amount || null,
                    subscriptionId: subscriptionData?.subscriptionId || subscriptionData?.subscription_id || null
                  });
                } catch (trackErr) {
                  console.warn('Failed to send conversion tracking:', trackErr);
                }
              } catch (assignErr) {
                console.warn('Failed to persist plan assignment:', assignErr);
              }

              try {
                const { toast } = await import('react-toastify');
                toast.success('Subscription activated! Your plan will auto-renew.');
              } catch (_) { }

              try {
                try { sessionStorage.removeItem('trial_invite'); } catch (_) { }
                try { localStorage.removeItem('trial_invite'); } catch (_) { }
                try { localStorage.removeItem('came_from_trial'); } catch (_) { }
                try { localStorage.removeItem('came_from_trial_token'); } catch (_) { }
                try { localStorage.removeItem('came_from_trial_sig'); } catch (_) { }
                try { localStorage.removeItem('came_from_trial_location'); } catch (_) { }
                try { localStorage.removeItem('came_from_trial_ts'); } catch (_) { }
                try { window.dispatchEvent(new Event('trialInviteSet')); } catch (_) { }
              } catch (_) { }
              // Show congrats modal with plan features
              try {
                const planKey = (planId || 'basic').toLowerCase();
                const cfg = PLAN_FEATURES[planKey] || PLAN_FEATURES.basic;
                setCongratsPlan(planKey);
                setCongratsPlanLabel(cfg.label || planKey.toUpperCase());
                setCongratsFeatures(cfg.features || []);
                setCongratsOpen(true);
              } catch (_) {
                try { navigate('/client-onboarding'); } catch (_) { }
              }
              onClose({ success: true, result: { subscriptionId: subscriptionData.subscriptionId || subscriptionData.subscription_id, paymentId: response.razorpay_payment_id, type: 'subscription' } });
            } catch (verifyErr) {
              console.warn('Failed to verify payment:', verifyErr);
              setError(verifyErr?.message || 'Payment succeeded but verification failed. We will process it shortly.');
              onClose && onClose({ success: false, error: 'verification_failed' });
            } finally {
              setProcessingPayment(false);
            }
          },
          modal: {
            ondismiss: () => {
              setError('Subscription setup was cancelled');
              setLoading(false);
            }
          }
        };

        const rzp = new window.Razorpay(subscriptionOptions);
        rzp.open();
      } catch (err) {
        console.error('Checkout error:', err);
        setError(err.message || 'Payment failed');
      } finally {
        setLoading(false);
      }

    } catch (err) {
      console.error('Checkout error:', err);
      setError(err.message || 'Payment failed');
    } finally {
      setLoading(false);
    }
  };

  // Handler to continue trial flow after user confirms in styled dialog
  const handleConfirmStartTrial = async () => {
    if (!trialConfirmData) return;
    setTrialConfirmOpen(false);
    setLoading(true);
    // Try to fetch existing mandate/subscription status to show autopay info
    try {
      const subscriptionId = trialConfirmData?.resp?.subscriptionId;
      if (subscriptionId) {
        try {
          const mres = await fetch(`/api/v1/customer/subscription/mandate-status/${subscriptionId}`, { credentials: 'include' });
          const mdata = await mres.json();
          if (mdata) setMandateInfo(mdata);
        } catch (mf) {
          console.warn('Failed to fetch mandate status', mf);
        }
      }
    } catch (e) {
      console.warn('Mandate status check failed', e);
    }
    try {
      const { resp, trialAmount, cycleLabel } = trialConfirmData;
      await PaymentService.loadRazorpayScript();

      const logoUrl = getLogoUrl();
      const planDisplayAmount = resolveAmount(trialConfirmData.planId, trialConfirmData.billingCycle) || 0;
      const planCycleLabel = cycleLabel;
      const subsOptions = {
        key: resp.keyId || resp.key_id || (window && window.__RAZORPAY_KEY__),
        subscription_id: resp.subscriptionId,
        name: (trialConfirmData.planId || 'Visibeen'),
        description: (trialAmount === 1 || trialConfirmData?.trialAmount === 1 || trialTokenApplied)
          ? `Trial: ₹1 today (verification). ${planCycleLabel} — ₹${Number(planDisplayAmount).toLocaleString('en-IN')} billed after ${trialConfirmData.trialDays} days.`
          : `Trial: ₹${trialAmount} today (verification). ${planCycleLabel} — ₹${Number(planDisplayAmount).toLocaleString('en-IN')} billed after ${trialConfirmData.trialDays} days.`,
        image: logoUrl || undefined,
        prefill: { email: payeeGmail, contact: payeeMobile },
        theme: { color: theme.palette.primary.main },
        recurring: true,
        handler: async (response) => {
          setProcessingMessage('Verifying trial payment and activating your trial access...');
          setProcessingProgress(5);
          setProcessingPayment(true);
          try {
            const verifyResp = await PaymentService.verifySubscriptionPayment({
              razorpay_subscription_id: response.razorpay_subscription_id || resp.subscriptionId,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature
            });
            try { const { toast } = await import('react-toastify'); if (verifyResp?.whitelisted) { toast.info(`Trial activated — access granted to ${verifyResp?.email || 'your email'}`); } } catch (_) { }
            try {
              // Clear trial markers on successful trial payment
              try { sessionStorage.removeItem('trial_invite'); } catch (_) { }
              try { localStorage.removeItem('trial_invite'); } catch (_) { }
              try { localStorage.removeItem('came_from_trial'); } catch (_) { }
              try { localStorage.removeItem('came_from_trial_token'); } catch (_) { }
              try { localStorage.removeItem('came_from_trial_sig'); } catch (_) { }
              try { localStorage.removeItem('came_from_trial_location'); } catch (_) { }
              try { localStorage.removeItem('came_from_trial_ts'); } catch (_) { }
              try { window.dispatchEvent(new Event('trialInviteSet')); } catch (_) { }
            } catch (_) { }
            try {
              const planKey = (trialConfirmData?.planId || planId || 'basic').toLowerCase();
              const cfg = PLAN_FEATURES[planKey] || PLAN_FEATURES.basic;
              setCongratsPlan(planKey);
              setCongratsPlanLabel(cfg.label || planKey.toUpperCase());
              setCongratsFeatures(cfg.features || []);
              setCongratsOpen(true);
            } catch (_) {
              try { navigate('/dashboard'); } catch (_) { }
            }
            onClose && onClose({ success: true, result: { subscriptionId: resp.subscriptionId, paymentId: response.razorpay_payment_id, type: 'trial' } });
          } catch (verifyErr) {
            console.warn('Failed to verify trial subscription payment:', verifyErr);
            setError(verifyErr?.message || 'Trial payment verified but server processing failed.');
            onClose && onClose({ success: false, error: 'verification_failed' });
          } finally {
            setProcessingPayment(false);
          }
        },
        modal: {
          ondismiss: () => {
            setError('Trial payment was cancelled');
            setLoading(false);
          }
        }
      };

      const rzp = new window.Razorpay(subsOptions);
      rzp.open();
    } catch (e) {
      console.error('Failed to open Razorpay checkout for trial:', e);
      setError('Failed to open payment gateway');
    } finally {
      setLoading(false);
      setTrialConfirmData(null);
    }
  };

  return (<>
    <Dialog
      open={Boolean(open)}
      onClose={(event, reason) => {
        // Prevent closing on backdrop click to ensure user fills required fields
        if (reason === 'backdropClick') return;
        onClose && onClose({ success: false });
      }}
      maxWidth="lg"
      fullWidth
      PaperProps={{ sx: { borderRadius: { xs: 3, md: 6 }, overflow: 'hidden', m: { xs: 1, md: 3 }, maxHeight: { xs: '95vh', md: '90vh' } } }}
    >
      <Box sx={{ background: 'linear-gradient(90deg,#0B91D6 0%, #475BE8 100%)', p: { xs: 1.5, md: 2 }, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: { xs: 1, md: 2 }, flexWrap: { xs: 'wrap', md: 'nowrap' } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, md: 2 }, minWidth: { xs: 80, md: 140 } }}>
          <Box component="img" src="/images/logo.svg" alt="Visibeen" sx={{ width: { xs: 70, md: 140 }, height: 'auto', objectFit: 'contain' }} />
        </Box>

        <Box sx={{ flex: 1, textAlign: 'center', px: { xs: 1, md: 2 } }}>
          <Typography sx={{ color: '#fff', fontWeight: 900, fontSize: { xs: 13, md: 18 }, letterSpacing: 0.4 }}>Secure Checkout</Typography>
          <Typography sx={{ color: 'rgba(255,255,255,0.98)', fontSize: { xs: 10, md: 13 }, mt: 0.25, fontWeight: 800, display: { xs: 'none', sm: 'block' } }}>🔒 Encrypted payments — Secure & private 💳</Typography>
        </Box>

        <Box sx={{ minWidth: { xs: 0, md: 120 }, display: { xs: 'none', md: 'block' } }} />
      </Box>
      <DialogContent sx={{ p: { xs: 1.5, md: 4 }, backgroundColor: '#f6f8fb' }}>
        <Dialog open={processingPayment} onClose={() => { }} fullWidth maxWidth="sm">
          <DialogTitle sx={{ fontSize: { xs: 16, md: 20 } }}>Activating access</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', gap: { xs: 2, md: 3 }, alignItems: 'center', p: { xs: 1, md: 2 }, minWidth: { xs: 'auto', md: 420 }, flexDirection: { xs: 'column', sm: 'row' } }}>
              <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', width: { xs: 80, md: 110 }, height: { xs: 80, md: 110 } }}>
                {processingAnimationData ? (
                  <Box sx={{ width: { xs: 80, md: 110 }, height: { xs: 80, md: 110 } }}>
                    <Lottie animationData={processingAnimationData} loop={true} />
                  </Box>
                ) : (
                  <CircularProgress variant="determinate" value={processingProgress} size={96} thickness={6} sx={{ color: theme.palette.primary.main }} />
                )}

                <Box sx={{ position: 'absolute', textAlign: 'center' }}>
                  {processingProgress >= 100 ? (
                    <CheckCircleIcon sx={{ color: 'success.main', fontSize: 40 }} />
                  ) : (
                    <Typography sx={{ fontWeight: 800, fontSize: 15 }}>{Math.min(100, Math.round(processingProgress))}%</Typography>
                  )}
                </Box>
              </Box>

              <Box sx={{ flex: 1, textAlign: { xs: 'center', sm: 'left' } }}>
                <Typography sx={{ fontWeight: 800, mb: 0.5, fontSize: { xs: 14, md: 16 } }}>{processingMessage}</Typography>
                <Typography sx={{ color: theme.palette.text.primary, fontSize: { xs: 11, md: 13 }, mb: 1 }}>This may take a few seconds. Do not close this window.</Typography>

                {/* Step indicators */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {[
                    { key: 'verifying', label: 'Verifying payment' },
                    { key: 'activating', label: 'Activating access' },
                    { key: 'sync', label: 'Syncing Business Profile' }
                  ].map((s, i) => {
                    const stepIndex = processingProgress < 40 ? 0 : (processingProgress < 80 ? 1 : 2);
                    const status = stepIndex > i ? 'done' : (stepIndex === i ? 'current' : 'pending');
                    return (
                      <Box key={s.key} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ width: 28, height: 28, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: status === 'done' ? theme.palette.success.main : (status === 'current' ? theme.palette.primary.main : '#e6eef9'), color: status === 'done' ? '#fff' : (status === 'current' ? '#fff' : theme.palette.text.primary) }}>
                          {status === 'done' ? <CheckCircleIcon sx={{ fontSize: 16 }} /> : (status === 'current' ? <CircularProgress size={16} thickness={4} sx={{ color: '#fff' }} /> : <Box sx={{ width: 8, height: 8, borderRadius: '50%', background: '#9aa9b2' }} />)}
                        </Box>
                        <Box>
                          <Typography sx={{ fontWeight: status === 'current' ? 700 : 600, fontSize: 14 }}>{s.label}</Typography>
                          {status === 'current' && <Typography sx={{ fontSize: 12, color: '#607d8b' }}>Processing…</Typography>}
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            </Box>
          </DialogContent>
        </Dialog>
        {loading && <Box display="flex" justifyContent="center" my={2}><CircularProgress /></Box>}
        {!loading && error && <Typography color="error" sx={{ mb: 2, fontSize: { xs: 13, md: 14 } }}>{error}</Typography>}
        {!loading && !error && (
          <Grid container spacing={{ xs: 2, md: 3 }} alignItems="stretch" sx={{ flexWrap: { xs: 'wrap', md: 'nowrap' }, overflowX: { xs: 'visible', md: 'auto' }, gap: { xs: 0, md: 2 } }}>
            <Grid item xs={12} md={4} sx={{ flex: { xs: '1 1 100%', md: '0 0 30%' }, minWidth: { xs: '100%', md: 260 }, maxWidth: { md: '32%' }, order: { xs: 2, md: 1 } }}>
              <Paper elevation={2} sx={{ p: { xs: 1.5, md: 2 }, borderRadius: { xs: 4, md: 6 }, maxHeight: { xs: '40vh', md: '70vh' }, overflowY: 'auto', width: '100%', border: '1px solid #eef6ff' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1, fontSize: { xs: 14, md: 16 } }}>Select a Google Business Profile</Typography>
                {profiles.length === 0 && <Typography sx={{ fontSize: { xs: 13, md: 14 } }}>No business profiles found for your account.</Typography>}
                {profiles.length > 0 && (
                  <List sx={{ display: 'grid', gap: 1, pr: 1 }}>
                    {profiles.map((p) => {
                      const pid = p.id || p.location_id || p.locationId;
                      const isSelected = String(selectedProfileId) === String(pid);
                      const displayEmail = p.email || p.gmail || p.ownerEmail || '';
                      const displayPhone = p.primaryPhone || p.phone || p.telephone || '';
                      const address = p.address || p.formattedAddress || p.locationAddress || '';
                      const hasPlan = Boolean(p.hasActivePlan);
                      const activePlanLabel = formatPlanLabel(p.activePlanDetails?.plan || p.plan || p.planName || 'Active plan');
                      const expiresAt = p.activePlanDetails?.expiresAt || p.expiresAt || p.expiration;
                      return (
                        <ListItem key={pid} onClick={() => handleProfileClick(p)} button sx={{ p: 0 }}>
                          <Paper elevation={isSelected ? 8 : 1} sx={{ width: '100%', p: 1, display: 'flex', gap: 1, alignItems: 'center', border: isSelected ? `2px solid ${theme.palette.primary.main}` : '1px solid #eef2f7', borderRadius: 6, transition: 'transform 0.12s, box-shadow 0.12s', '&:hover': { transform: 'translateY(-2px)', boxShadow: 6 }, cursor: 'pointer', background: '#fff' }}>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography sx={{ fontWeight: 700, fontSize: 14, lineHeight: '1.1' }}>{p.name || p.title || p.business_name}</Typography>
                              <Typography sx={{ fontSize: 12, color: theme.palette.text.primary, mt: 0.5, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{address}</Typography>
                              <Box sx={{ display: 'flex', gap: 1.25, alignItems: 'center', mt: 0.5, flexWrap: 'wrap' }}>
                                {displayEmail ? (
                                  <Typography sx={{ fontSize: 12, color: '#556270', maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{displayEmail}</Typography>
                                ) : null}
                                {displayPhone ? (
                                  <Typography sx={{ fontSize: 12, color: '#556270', maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{displayPhone}</Typography>
                                ) : null}
                              </Box>
                            </Box>
                            <Box sx={{ textAlign: 'right', minWidth: 56 }}>
                              <Typography sx={{ fontWeight: 700, color: theme.palette.primary.main, fontSize: 14 }}>{p.totalLocations || p.location_count || '-'}</Typography>
                            </Box>
                          </Paper>
                        </ListItem>
                      );
                    })}
                  </List>
                )}
              </Paper>
            </Grid>
            <Grid item xs={12} md={8} sx={{ flex: { xs: '1 1 100%', md: '0 0 70%' }, minWidth: { xs: '100%', md: 420 }, alignSelf: 'flex-start', order: { xs: 1, md: 2 } }}>
              <Paper elevation={6} sx={{ p: { xs: 2, md: 3 }, borderRadius: { xs: 4, md: 6 }, background: '#ffffff', border: '1px solid #eef6ff', position: { md: 'sticky' }, top: { md: 24 }, minWidth: { xs: 'auto', md: 420 }, boxShadow: '0 6px 24px rgba(71,91,232,0.06)' }}>
                <Typography variant="h6" sx={{ fontWeight: 800, mb: 1, fontSize: { xs: 16, md: 20 } }}>Payment Summary</Typography>
                <Typography sx={{ fontSize: { xs: 11, md: 13 }, color: theme.palette.text.primary, mb: 2 }}>Secure checkout powered by Razorpay. We will never share your payment info.</Typography>
                <Divider sx={{ mb: 2 }} />
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <TextField
                    label="Payee Gmail"
                    value={payeeGmail}
                    onChange={(e) => {
                      setPayeeGmail(e.target.value);
                      if (emailError) setEmailError('');
                    }}
                    fullWidth
                    required
                    error={Boolean(emailError)}
                    helperText={emailError || (sessionEmail ? `Using account email: ${sessionEmail}` : '')}
                    placeholder="example@gmail.com"
                    InputProps={{ startAdornment: (<InputAdornment position="start"><EmailIcon /></InputAdornment>) }}
                    sx={{ background: '#ffffff', borderRadius: 2, '& .MuiOutlinedInput-root': { paddingLeft: 1 } }}
                  />
                  <TextField
                    label="Payee Mobile"
                    value={payeeMobile}
                    onChange={(e) => {
                      setPayeeMobile(e.target.value);
                      if (phoneError) setPhoneError('');
                    }}
                    fullWidth
                    required
                    error={Boolean(phoneError)}
                    helperText={phoneError}
                    placeholder="+91 98xxxx...."
                    InputProps={{ startAdornment: (<InputAdornment position="start"><PhoneIcon /></InputAdornment>) }}
                    sx={{ background: '#ffffff', borderRadius: 2, '& .MuiOutlinedInput-root': { paddingLeft: 1 } }}
                  />
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography sx={{ fontWeight: 700 }}>Add number to GMB</Typography>
                      <Chip label="Popular" size="small" sx={{ height: 18, fontSize: 10, background: '#eff6ff', color: '#2563eb', fontWeight: 800 }} />
                    </Box>
                    <FormControlLabel
                      control={<Switch checked={addNumberToGMB} onChange={(e) => setAddNumberToGMB(e.target.checked)} color="primary" />}
                      label={<Typography sx={{ fontWeight: 800, color: addNumberToGMB ? theme.palette.primary.main : '#475569' }}>₹5,000</Typography>}
                      sx={{ ml: 0 }}
                    />
                  </Box>

                  {/* Inline Info Box (Replaces Popup) */}
                  <Collapse in={addNumberToGMB}>
                    <Box sx={{ p: 2, background: '#eff6ff', borderRadius: 3, border: '1px solid #bfdbfe' }}>
                      <Typography variant="caption" sx={{ fontWeight: 800, color: '#1e40af', mb: 1.5, display: 'flex', alignItems: 'center', gap: 1, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                        <CheckCircleIcon sx={{ fontSize: 16 }} /> Requirements For Update
                      </Typography>
                      <Box sx={{ display: 'grid', gap: 1.2, mb: 1.5 }}>
                        {[
                          'GST / Udhyam Certificate copy',
                          '2 premises photos (different angles)',
                          '3 street view photos with neighbors',
                          'Number on shop board & visiting card',
                          'Website (Optional)'
                        ].map((text, i) => (
                          <Box key={i} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                            <Box sx={{ width: 5, height: 5, borderRadius: '50%', background: '#2563eb', mt: 0.7, flexShrink: 0 }} />
                            <Typography variant="caption" sx={{ color: '#1e3a8a', fontWeight: 700, lineHeight: 1.2, fontSize: '11px' }}>
                              {text}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                      <Typography variant="caption" sx={{ color: '#2563eb', fontWeight: 700, fontSize: '10px', fontStyle: 'italic', display: 'block', textAlign: 'center' }}>
                        📞 Our experts will call you to collect these details.
                      </Typography>
                    </Box>
                  </Collapse>

                  <Divider />

                  <Box sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: { xs: 'wrap', md: 'nowrap' } }}>
                    <Typography sx={{ color: theme.palette.text.primary, fontSize: { xs: 13, md: 14 } }}>Plan</Typography>
                    <Typography sx={{ fontWeight: 800, fontSize: { xs: 13, md: 14 } }}>{planId ? planId.toUpperCase() : '—'}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: { xs: 'wrap', md: 'nowrap' } }}>
                    <Typography sx={{ color: theme.palette.text.primary, fontSize: { xs: 13, md: 14 } }}>Billing</Typography>
                    <Typography sx={{ fontWeight: 700, color: '#2e7d32', fontSize: { xs: 13, md: 14 } }}>
                      {billingCycle === 'monthly' ? 'Monthly (Auto-renew)' : 'Yearly (Auto-renew)'}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: { xs: 'wrap', md: 'nowrap' } }}>
                    <Typography sx={{ color: theme.palette.text.primary, fontSize: { xs: 13, md: 14 } }}>Base Amount</Typography>
                    <Typography sx={{ fontWeight: 700, fontSize: { xs: 13, md: 14 } }}>₹{baseAmount.toLocaleString('en-IN')}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: { xs: 'wrap', md: 'nowrap' } }}>
                    <Typography sx={{ color: theme.palette.text.primary, fontSize: { xs: 13, md: 14 } }}>GMB Number Add-on</Typography>
                    <Typography sx={{ fontWeight: 700, fontSize: { xs: 13, md: 14 } }}>{addNumberToGMB ? '₹5,000' : '₹0'}</Typography>
                  </Box>

                  {/* Coupon Code Section */}
                  <CouponContainer>
                    {!showTrialField && !trialTokenApplied ? (
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        <Button
                          variant="text"
                          startIcon={<LocalOfferIcon />}
                          onClick={() => setShowTrialField(true)}
                          sx={{
                            color: '#0B91D6',
                            fontWeight: 600,
                            textTransform: 'none',
                            p: 0,
                            '&:hover': { backgroundColor: 'transparent' }
                          }}
                        >
                          Have a trial coupon?
                        </Button>
                      </Box>
                    ) : trialTokenApplied ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CheckCircleIcon sx={{ color: 'success.main', fontSize: 20 }} />
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 700, color: 'success.main' }}>
                              {trialTokenApplied} Applied (trial)
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              {trialMessage || 'Trial coupon applied — verification required'}
                            </Typography>
                          </Box>
                        </Box>
                        <IconButton
                          size="small"
                          onClick={handleRemoveTrial}
                          sx={{ color: '#999' }}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    ) : (
                      <Box>
                        <Collapse in={showTrialField}>
                          <Box sx={{ mt: 1 }}>
                            <Typography variant="body2" sx={{ mb: 1, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <LocalOfferIcon sx={{ fontSize: 16, color: '#0B91D6' }} />
                              Apply Trial Coupon
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                              <TextField
                                size="small"
                                placeholder="Enter trial code"
                                value={trialCode}
                                onChange={(e) => { setTrialCode(e.target.value); setTrialError(''); }}
                                onKeyPress={(e) => { if (e.key === 'Enter') handleApplyTrialCoupon(); }}
                                sx={{
                                  flex: 1,
                                  '& .MuiOutlinedInput-root': {
                                    background: '#fff',
                                    borderRadius: '8px'
                                  },
                                  '& .MuiInputBase-input': {
                                    fontWeight: 600,
                                    letterSpacing: '1px'
                                  }
                                }}
                                InputProps={{
                                  startAdornment: (
                                    <InputAdornment position="start">
                                      <LocalOfferIcon sx={{ color: '#999', fontSize: 18 }} />
                                    </InputAdornment>
                                  )
                                }}
                              />
                              <ApplyButton onClick={handleApplyTrialCoupon} disabled={trialLoading}>
                                Apply
                              </ApplyButton>
                            </Box>
                            {trialError && (
                              <Typography variant="caption" sx={{ color: '#ef4444', mt: 0.5, display: 'block' }}>{trialError}</Typography>
                            )}
                            {trialMessage && (
                              <Typography variant="caption" sx={{ color: trialSuccess ? 'success.main' : '#ef4444', mt: 0.5, display: 'block' }}>{trialMessage}</Typography>
                            )}
                            <Button size="small" variant="text" onClick={() => { setShowTrialField(false); setTrialCode(''); setTrialError(''); }} sx={{ mt: 0.5, color: '#666', textTransform: 'none', fontSize: 12, p: 0 }}>Cancel</Button>
                          </Box>
                        </Collapse>
                      </Box>
                    )}
                  </CouponContainer>

                  <Divider />

                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: { xs: 'wrap', md: 'nowrap' }, gap: { xs: 1, md: 0 } }}>
                    <Typography sx={{ fontSize: { xs: 14, md: 16 } }}>Total</Typography>
                    <Box sx={{ textAlign: 'right', width: { xs: '100%', md: 'auto' } }}>
                      <Box sx={{ display: 'inline-block', px: { xs: 1.5, md: 2 }, py: 1, borderRadius: 4, background: 'linear-gradient(90deg,#f0f7ff 0%, #ffffff 100%)' }}>
                        <Typography sx={{ fontSize: { xs: 18, md: 22 }, fontWeight: 900, color: theme.palette.primary.dark }}>
                          {isTrialCheckout ? `₹1` : `₹${computedTotal.toLocaleString('en-IN')}`}
                        </Typography>
                      </Box>
                      {isTrialCheckout && (
                        <Typography sx={{ fontSize: { xs: 11, md: 13 }, color: theme.palette.text.primary, mt: 0.5 }}>
                          1 rupee now — ₹{planAmount.toLocaleString('en-IN')} after {trialDays} days ({firstChargeDateStr})
                        </Typography>
                      )}
                    </Box>
                  </Box>

                  {/* Autopay info note */}
                  <Box sx={{
                    backgroundColor: theme.palette.background.paper,
                    borderRadius: 2,
                    p: { xs: 1, md: 1.5 },
                    border: `1px solid ${theme.palette.primary.main}`
                  }}>
                    <Typography sx={{ fontSize: { xs: 11, md: 12 }, color: theme.palette.primary.main, fontWeight: 500 }}>
                      🔄 Your subscription will auto-renew {billingCycle === 'monthly' ? 'every month' : 'every year'}.
                      You can cancel anytime from your account settings.
                    </Typography>
                  </Box>

                  <Button
                    variant="contained"
                    onClick={handleBuy}
                    disabled={loading || profiles.length === 0}
                    fullWidth
                    sx={{ mt: 1, py: { xs: 1, md: 1.5 }, borderRadius: { xs: 4, md: 6 }, background: 'linear-gradient(90deg,#0B91D6 0%, #475BE8 100%)', color: '#fff', '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 16px 40px rgba(71,91,232,0.16)' }, boxShadow: '0 12px 28px rgba(71,91,232,0.12)', fontWeight: 800, fontSize: { xs: 12, md: 14 } }}
                  >
                    {loading ? 'Processing...' : (isTrialCheckout ? `Pay ₹1 now — ₹${planAmount.toLocaleString('en-IN')} on ${firstChargeDateStr}` : `Subscribe ₹${computedTotal.toLocaleString('en-IN')}/${billingCycle === 'monthly' ? 'mo' : 'yr'}`)}
                  </Button>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        )}
      </DialogContent>
      {/* Styled confirmation dialog for trial flow (replaces window.confirm) */}
      <Dialog open={Boolean(trialConfirmOpen)} onClose={() => { if (!loading) setTrialConfirmOpen(false); }} maxWidth="md" fullWidth PaperProps={{ sx: { m: { xs: 1, md: 3 }, borderRadius: { xs: 3, md: 4 } } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: { xs: 2, md: 3 }, pt: 2, flexWrap: { xs: 'wrap', md: 'nowrap' }, gap: { xs: 1, md: 2 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, md: 2 } }}>
            <Avatar sx={{ bgcolor: 'primary.main', width: { xs: 40, md: 48 }, height: { xs: 40, md: 48 } }}>
              <AccessTimeIcon sx={{ color: '#fff', fontSize: { xs: 20, md: 24 } }} />
            </Avatar>
            <Box>
              <DialogTitle sx={{ m: 0, p: 0, fontSize: { xs: 16, md: 20 } }}>Start your 7‑day trial</DialogTitle>
              <Typography variant="caption" sx={{ color: theme.palette.text.primary, fontSize: { xs: 10, md: 12 } }}>Instant access — verification ₹{trialConfirmData?.trialAmount || 1}</Typography>
            </Box>
          </Box>
          <IconButton onClick={() => { if (!loading) setTrialConfirmOpen(false); }} size="small"><CloseIcon /></IconButton>
        </Box>

        <DialogContent sx={{ pb: 0, px: { xs: 2, md: 3 } }}>
          {trialConfirmData && (
            <Grid container spacing={{ xs: 2, md: 3 }} sx={{ pt: 1 }}>
              <Grid item xs={12} md={5}>
                <Paper elevation={3} sx={{ p: { xs: 1.5, md: 2 }, borderRadius: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, flexWrap: { xs: 'wrap', md: 'nowrap' }, gap: 1 }}>
                    <Box>
                      <Typography sx={{ fontWeight: 800, fontSize: { xs: 14, md: 16 } }}>{(trialConfirmData.planId || '').toUpperCase() || 'BASIC'}</Typography>
                      <Typography variant="caption" sx={{ color: theme.palette.text.primary, fontSize: { xs: 10, md: 12 } }}>{trialConfirmData.cycleLabel} plan · Auto‑renew</Typography>
                    </Box>
                    <Chip label={`${trialConfirmData.trialDays}-day trial`} color="primary" sx={{ fontWeight: 800, fontSize: { xs: 10, md: 12 } }} size="small" />
                  </Box>

                  <Divider sx={{ mb: 2 }} />

                  <Box sx={{ display: 'flex', gap: { xs: 1, md: 2 }, alignItems: 'center', mb: 1, flexWrap: { xs: 'wrap', md: 'nowrap' } }}>
                    <Typography sx={{ fontSize: { xs: 18, md: 22 }, fontWeight: 900 }}>₹{trialConfirmData.trialAmount}</Typography>
                    <Box sx={{ color: theme.palette.text.primary }}>
                      <Typography variant="caption" sx={{ fontSize: { xs: 10, md: 12 }, display: 'block' }}>Today (verification)</Typography>
                      <Typography variant="caption" sx={{ fontSize: { xs: 10, md: 12 }, display: 'block' }}>First full charge: ₹{trialConfirmData.planAmount} on {trialConfirmData.dateStr}</Typography>
                    </Box>
                  </Box>

                  <Box sx={{ mt: 1 }}>
                    <Typography sx={{ fontSize: { xs: 11, md: 13 }, color: '#42535b' }}>What you get during trial</Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1 }}>
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}><BeenhereIcon sx={{ color: 'success.main', fontSize: { xs: 18, md: 24 } }} /><Typography sx={{ fontSize: { xs: 11, md: 13 } }}>Full Basic plan features</Typography></Box>
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}><CheckCircleIcon sx={{ color: 'primary.main', fontSize: { xs: 18, md: 24 } }} /><Typography sx={{ fontSize: { xs: 11, md: 13 } }}>GMB profile sync & reporting</Typography></Box>
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}><AccessTimeIcon sx={{ color: '#f59e0b', fontSize: { xs: 18, md: 24 } }} /><Typography sx={{ fontSize: { xs: 11, md: 13 } }}>Cancel anytime before first charge</Typography></Box>
                    </Box>
                  </Box>
                </Paper>
              </Grid>

              <Grid item xs={12} md={7}>
                <Paper elevation={0} sx={{ p: { xs: 1.5, md: 2 } }}>
                  <Typography sx={{ fontWeight: 700, mb: 1, fontSize: { xs: 14, md: 16 } }}>Secure & easy</Typography>
                  <Typography sx={{ color: theme.palette.text.primary, fontSize: { xs: 11, md: 13 }, mb: 2 }}>We only take a small verification charge to set up autopay. Your payment method will only be charged the full amount after the trial unless you cancel.</Typography>

                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2, fontSize: { xs: 12, md: 14 } }}>
                    <Typography sx={{ color: theme.palette.text.primary, fontSize: 'inherit' }}>Verification charge</Typography>
                    <Typography sx={{ fontWeight: 800, fontSize: 'inherit' }}>₹{trialConfirmData.trialAmount}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2, fontSize: { xs: 12, md: 14 } }}>
                    <Typography sx={{ color: theme.palette.text.primary, fontSize: 'inherit' }}>First full charge</Typography>
                    <Typography sx={{ fontWeight: 800, fontSize: 'inherit' }}>₹{trialConfirmData.planAmount} on {trialConfirmData.dateStr}</Typography>
                  </Box>

                  {mandateInfo && (
                    <Box sx={{ mb: 2, p: 1, borderRadius: 1, backgroundColor: '#fbfdff', border: '1px solid #eef2f7' }}>
                      <Typography sx={{ fontSize: 12, color: theme.palette.text.primary }}>Autopay / Mandate status</Typography>
                      <Typography sx={{ fontWeight: 700, color: mandateInfo.autopayActive ? 'success.main' : 'error.main' }}>
                        {mandateInfo.autopayActive ? 'Autopay active (not cancelled)' : 'Autopay cancelled'}
                      </Typography>
                      {mandateInfo.mandateStatus && (
                        <Typography variant="caption" sx={{ color: theme.palette.text.primary }}>{`Mandate: ${mandateInfo.mandateStatus}`}</Typography>
                      )}
                    </Box>
                  )}

                  <Divider sx={{ my: 2 }} />

                  <Box sx={{ display: 'flex', gap: { xs: 1, md: 2 }, mb: 2, flexWrap: { xs: 'wrap', md: 'nowrap' } }}>
                    <Paper variant="outlined" sx={{ p: 1, flex: 1, borderRadius: 2, minWidth: { xs: '45%', md: 'auto' } }}>
                      <Typography sx={{ fontSize: { xs: 10, md: 12 }, color: theme.palette.text.primary }}>Auto‑renew</Typography>
                      <Typography sx={{ fontWeight: 700, fontSize: { xs: 12, md: 14 } }}>On</Typography>
                    </Paper>
                    <Paper variant="outlined" sx={{ p: 1, flex: 1, borderRadius: 2, minWidth: { xs: '45%', md: 'auto' } }}>
                      <Typography sx={{ fontSize: { xs: 10, md: 12 }, color: theme.palette.text.primary }}>Manage</Typography>
                      <Typography sx={{ fontWeight: 700, fontSize: { xs: 12, md: 14 } }}>Account settings</Typography>
                    </Paper>
                  </Box>

                  <Typography variant="caption" sx={{ color: theme.palette.text.primary, display: 'block', mb: 2, fontSize: { xs: 10, md: 12 } }}>By continuing you agree to our Terms of Service and authorize us to charge the payment method as described above.</Typography>

                  <Box sx={{ display: 'flex', gap: { xs: 1, md: 2 }, flexWrap: { xs: 'wrap', md: 'nowrap' } }}>
                    <Button onClick={() => { if (!loading) setTrialConfirmOpen(false); }} disabled={loading} sx={{ flex: 1, minWidth: { xs: '100%', md: 'auto' }, order: { xs: 2, md: 1 }, fontSize: { xs: 12, md: 14 } }}>
                      Cancel
                    </Button>
                    <Button variant="contained" onClick={handleConfirmStartTrial} disabled={loading} sx={{ flex: 1, minWidth: { xs: '100%', md: 'auto' }, order: { xs: 1, md: 2 }, background: theme.palette.success.main, color: theme.palette.success.contrastText || '#fff', '&:hover': { background: theme.palette.success.dark }, fontWeight: 800, fontSize: { xs: 12, md: 14 } }}>
                      Continue & Verify
                    </Button>
                  </Box>
                </Paper>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <Snackbar
          open={planSnackbarOpen}
          autoHideDuration={4000}
          onClose={handlePlanSnackbarClose}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert onClose={handlePlanSnackbarClose} icon={<InfoOutlinedIcon />} severity="info" sx={{ width: '100%' }}>
            {planSnackbarMessage}
          </Alert>
        </Snackbar>
      </Dialog>

      <DialogActions sx={{ p: { xs: 1.5, md: 2 }, backgroundColor: '#fbfdff' }}>
        <Button onClick={() => onClose && onClose({ success: false })} disabled={loading} sx={{ fontSize: { xs: 12, md: 14 } }}>Cancel</Button>
      </DialogActions>

      <Box sx={{ textAlign: 'center', py: 1, px: 2, borderTop: '1px solid #eef2f7', backgroundColor: '#fbfdff' }}>
        <Typography variant="caption" sx={{ color: theme.palette.text.primary, fontWeight: 600, fontSize: { xs: 10, md: 12 } }}>🔒 Trusted & secured by Razorpay</Typography>
      </Box>
    </Dialog>
    <CongratsModal
      open={congratsOpen}
      plan={congratsPlan}
      planLabel={congratsPlanLabel}
      features={congratsFeatures}
      onClose={(action) => {
        setCongratsOpen(false);
        if (action === 'dashboard') {
          try { navigate('/dashboard'); } catch (_) { }
        }
      }}
    />
  </>);
};

export default CheckoutModal;